import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import {
  Search, BookOpen, ChevronDown, ChevronUp, ExternalLink, Sparkles, Bookmark,
  Library, ArrowRight, Star,
} from 'lucide-react';

const ALL_CATEGORIES = ['Mindset', 'Mental Health', 'Purpose', 'Growth', 'Leadership', 'Habits', 'Healing'];

export default function Bookshelf() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState('All');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('curated'); // curated | az | author
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get('/books').then(r => setBooks(r.data || [])).finally(() => setLoading(false));
  }, []);

  const mickeysOwn = useMemo(() => books.filter(b => b.is_mickeys_own), [books]);
  const others = useMemo(() => books.filter(b => !b.is_mickeys_own), [books]);

  const filtered = useMemo(() => {
    let list = others;
    if (cat !== 'All') list = list.filter(b => b.category === cat);
    if (q.trim()) {
      const ql = q.toLowerCase();
      list = list.filter(b => (b.title + ' ' + b.author + ' ' + (b.tags || []).join(' ')).toLowerCase().includes(ql));
    }
    if (sort === 'az') list = [...list].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    else if (sort === 'author') list = [...list].sort((a, b) => (a.author || '').localeCompare(b.author || ''));
    return list;
  }, [others, cat, q, sort]);

  // Build counts per category for chips
  const counts = useMemo(() => {
    const c = { All: others.length };
    ALL_CATEGORIES.forEach(cc => { c[cc] = others.filter(b => b.category === cc).length; });
    return c;
  }, [others]);

  return (
    <div className="bg-white">
      {/* Hero header */}
      <section className="relative bg-ink text-white py-14 md:py-20 overflow-hidden" data-testid="bookshelf-hero">
        <div className="absolute inset-0 pointer-events-none kc-noise opacity-50" />
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(700px 380px at 80% 50%, rgba(212,164,55,0.15), transparent 60%)',
        }} />
        <div className="relative kc-container grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--kc-gold)]/40 bg-[color:var(--kc-gold)]/10 text-[10px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold">
              <Library size={12} /> The Bookshelf
            </div>
            <h1 className="mt-4 font-display uppercase leading-[0.9]" style={{ fontSize: 'clamp(2.5rem, 6.5vw, 5.5rem)' }} data-testid="bookshelf-title">
              MICKEY’S<br /><span className="text-[color:var(--kc-gold)]">BOOKSHELF.</span>
            </h1>
            <p className="mt-4 text-lg text-white/80 max-w-xl leading-relaxed">
              The books that shaped my mindset. The ones I’d hand a younger version of myself in a heartbeat. No fluff — just the stuff that actually moved the needle.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[var(--kc-radius-md)] p-4 grid grid-cols-3 gap-2">
              {[
                { label: 'Books', value: books.length },
                { label: 'Categories', value: ALL_CATEGORIES.length },
                { label: 'Mickey’s own', value: mickeysOwn.length },
              ].map((s, i) => (
                <div key={i} className="text-center py-2">
                  <div className="font-display text-3xl text-[color:var(--kc-gold)]">{s.value}</div>
                  <div className="text-[10px] tracking-[0.18em] uppercase text-white/60 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MICKEY'S OWN BOOKS — featured section */}
      {mickeysOwn.length > 0 && (
        <section className="bg-white py-12 md:py-16 border-b border-kc" data-testid="bookshelf-mickeys-own">
          <div className="kc-container">
            <div className="flex items-end justify-between gap-3 mb-6">
              <div>
                <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)] font-semibold"><Star size={12} className="fill-[color:var(--kc-gold)]" /> Written by Mickey</div>
                <h2 className="mt-2 font-display uppercase leading-[0.92]" style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}>Mickey’s own books</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {mickeysOwn.map((b) => (
                <BookCard key={b.id} book={b} expanded={expanded[b.id]} onToggle={() => setExpanded({ ...expanded, [b.id]: !expanded[b.id] })} featured />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONTROLS — search, sort, category chips */}
      <section className="bg-surface-2 sticky top-16 z-20 border-b border-kc" data-testid="bookshelf-controls">
        <div className="kc-container py-3 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-kc" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, author, or tag…"
              className="w-full h-11 pl-10 pr-3 rounded-[var(--kc-radius-sm)] border border-kc-strong bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--kc-gold)]"
              data-testid="bookshelf-search"
            />
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-kc uppercase tracking-wider hidden md:inline">Sort</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 px-2 rounded-[var(--kc-radius-sm)] border border-kc-strong bg-white text-sm" data-testid="bookshelf-sort">
              <option value="curated">Mickey’s pick order</option>
              <option value="az">A → Z (title)</option>
              <option value="author">By author</option>
            </select>
          </div>
        </div>
        {/* Category chips */}
        <div className="kc-container pb-3 flex gap-2 overflow-x-auto" data-testid="bookshelf-categories">
          {['All', ...ALL_CATEGORIES].map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-3 h-8 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider rounded-full whitespace-nowrap transition-colors ${cat === c ? 'bg-ink text-white' : 'bg-white text-ink-2 border border-kc hover:bg-surface-2'}`}
              data-testid={`bookshelf-cat-${c}`}
            >
              {c} <span className="text-[10px] opacity-60">({counts[c] ?? 0})</span>
            </button>
          ))}
        </div>
      </section>

      {/* BOOKS GRID */}
      <section className="bg-white py-12 md:py-16">
        <div className="kc-container">
          {loading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="bookshelf-loading">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-surface-2 rounded-[var(--kc-radius-md)] h-80 animate-pulse" />
              ))}
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-20" data-testid="bookshelf-empty">
              <BookOpen className="mx-auto text-muted-kc mb-3" size={36} />
              <h3 className="font-display uppercase text-2xl">No books match your filter.</h3>
              <p className="mt-2 text-muted-kc">Try clearing the search or picking a different category.</p>
              <button onClick={() => { setCat('All'); setQ(''); }} className="mt-4 h-10 px-5 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold text-sm">Clear filters</button>
            </div>
          )}
          {!loading && filtered.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="bookshelf-grid">
              {filtered.map((b) => (
                <BookCard key={b.id} book={b} expanded={expanded[b.id]} onToggle={() => setExpanded({ ...expanded, [b.id]: !expanded[b.id] })} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-ink text-white py-10">
        <div className="kc-container flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[color:var(--kc-gold)]">Want more like this?</div>
            <div className="mt-1 font-display uppercase text-2xl md:text-3xl">Start with HEADSPACE — pay what you want.</div>
          </div>
          <Link to="/courses/headspace" className="h-12 px-6 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="bookshelf-cta">
            Start the course <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}

const BookCard = ({ book, expanded, onToggle, featured = false }) => {
  const [imgError, setImgError] = useState(false);
  const hasCover = !!book.cover_image && !imgError;
  const buyLink = book.affiliate_link || (book.title ? `https://www.amazon.com/s?k=${encodeURIComponent(book.title + ' ' + (book.author || ''))}` : '');

  return (
    <article className={`group relative bg-white border rounded-[var(--kc-radius-md)] overflow-hidden transition-all hover:-translate-y-0.5 ${featured ? 'border-[color:var(--kc-gold)] shadow-[0_20px_50px_-20px_rgba(212,164,55,0.35)]' : 'border-kc hover:border-[color:var(--kc-gold)] hover:shadow-[0_15px_40px_-15px_rgba(0,0,0,0.18)]'}`} data-testid={`book-card-${book.id}`}>
      {featured && (
        <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 bg-[color:var(--kc-gold)] text-black text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"><Star size={10} className="fill-current" /> Mickey’s book</div>
      )}
      <div className="flex flex-col h-full">
        <div className="flex gap-4 p-5">
          <div className="flex-shrink-0 w-24 aspect-[2/3] rounded-md overflow-hidden bg-ink shadow-lg border border-kc relative">
            {hasCover ? (
              <img src={book.cover_image} alt={book.title} onError={() => setImgError(true)} className="w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-2 text-white" style={{ background: 'linear-gradient(135deg, #0B0B0C 0%, #1A1A1C 60%, rgba(212,164,55,0.35) 100%)' }}>
                <BookOpen size={18} className="text-[color:var(--kc-gold)] mb-2 opacity-80" />
                <div className="font-display uppercase text-[10px] leading-tight text-center line-clamp-3">{book.title}</div>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.18em] uppercase text-[color:var(--kc-gold-2)] font-semibold" data-testid="book-category">{book.category}</div>
            <h3 className="mt-1 font-display uppercase text-lg leading-tight line-clamp-2" data-testid="book-title">{book.title}</h3>
            <div className="mt-1 text-xs text-muted-kc" data-testid="book-author">by {book.author}</div>
            {(book.tags || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {book.tags.slice(0, 3).map((t, i) => (
                  <span key={i} className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-surface-2 rounded text-muted-kc">{t}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        {book.why_mickey_recommends && (
          <div className="px-5 pb-3">
            <div className="border-l-2 border-[color:var(--kc-gold)] pl-3 italic text-sm text-ink-2 leading-relaxed" data-testid="book-why">
              <Sparkles size={12} className="inline text-[color:var(--kc-gold)] mr-1 -mt-0.5" />
              <span>{book.why_mickey_recommends}</span>
            </div>
          </div>
        )}

        {book.mickey_notes && (
          <div className="px-5 pb-2">
            <button onClick={onToggle} className="text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 text-ink hover:text-[color:var(--kc-gold-2)] transition-colors" data-testid={`book-notes-toggle-${book.id}`}>
              <Bookmark size={12} /> Mickey’s notes {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            {expanded && (
              <div className="mt-3 p-3 bg-surface-2 rounded-[var(--kc-radius-sm)] text-sm text-ink-2 leading-relaxed whitespace-pre-line" data-testid={`book-notes-${book.id}`}>
                {book.mickey_notes}
              </div>
            )}
          </div>
        )}

        <div className="mt-auto px-5 py-4 border-t border-kc flex items-center justify-between gap-2 bg-surface-2/50">
          {buyLink ? (
            <a href={buyLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-ink hover:text-[color:var(--kc-gold-2)] transition-colors" data-testid={`book-buy-${book.id}`}>
              {book.affiliate_link ? 'Get the book' : 'Find on Amazon'} <ExternalLink size={12} />
            </a>
          ) : <span />}
          {book.isbn && <span className="text-[10px] text-muted-kc font-mono tabular-nums">ISBN {book.isbn}</span>}
        </div>
      </div>
    </article>
  );
};
